/* eslint-disable react-refresh/only-export-components */
import type { ProofOfAgePayload } from '@/features/order/model'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { createContext, use, useCallback, useMemo, useRef, useState } from 'react'
import { CollectionCodeModal } from './CollectionCodeModal'

interface CollectionCodeModalState {
  opened: boolean
  orderId: string | null
  /** The orders this completion applies to; when omitted the modal falls back to the whole group. */
  orderIds?: string[]
  nextStatus: string | null
  lastUpdateTime?: string
  /** BD-8761: the Proof of Age record to submit with the code, when the checks ran first. */
  proofOfAge?: ProofOfAgePayload
}

interface OpenCollectionCodeParams {
  orderId: string
  orderIds?: string[]
  nextStatus: string
  lastUpdateTime?: string
  proofOfAge?: ProofOfAgePayload
}

interface CollectionCodeContextValue {
  open: (params: OpenCollectionCodeParams) => void
  close: () => void
}

const CollectionCodeContext = createContext<CollectionCodeContextValue | null>(null)

export function CollectionCodeProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<CollectionCodeModalState>({
    opened: false,
    orderId: null,
    nextStatus: null,
  })

  const navigate = useNavigate()
  const location = useLocation()
  const locationRef = useRef(location)
  locationRef.current = location

  const savedOrderSearch = useRef<Record<string, string> | null>(null)

  const open = useCallback(({ orderId, orderIds, nextStatus, lastUpdateTime, proofOfAge }: OpenCollectionCodeParams) => {
    const currentSearch = locationRef.current.search as Record<string, string>
    if (currentSearch.orderId) {
      savedOrderSearch.current = currentSearch
      navigate({
        to: locationRef.current.pathname,
        search: {},
        resetScroll: false,
      })
    }
    setState({ opened: true, orderId, orderIds, nextStatus, lastUpdateTime, proofOfAge })
  }, [navigate])

  const close = useCallback(() => {
    setState(s => ({ ...s, opened: false }))
    if (savedOrderSearch.current) {
      const search = savedOrderSearch.current
      savedOrderSearch.current = null
      navigate({
        to: locationRef.current.pathname,
        search,
        resetScroll: false,
      })
    }
  }, [navigate])

  function handleSuccess() {
    savedOrderSearch.current = null
    setState(s => ({ ...s, opened: false }))
  }

  const contextValue = useMemo(() => ({ open, close }), [close, open])

  return (
    <CollectionCodeContext value={contextValue}>
      {children}
      {state.orderId && (
        <CollectionCodeModal
          opened={state.opened}
          onClose={close}
          onSuccess={handleSuccess}
          orderId={state.orderId}
          orderIds={state.orderIds}
          nextStatus={state.nextStatus!}
          lastUpdateTime={state.lastUpdateTime}
          proofOfAge={state.proofOfAge}
        />
      )}
    </CollectionCodeContext>
  )
}

export function useCollectionCodeModal() {
  const context = use(CollectionCodeContext)
  if (!context) {
    throw new Error('useCollectionCodeModal must be used within a CollectionCodeProvider')
  }
  return context
}
